/**
 * Shared grade helpers for the lesson Practice tab (quiz + flashcards).
 *
 * FSRS grades are 1-4 (Again / Hard / Good / Easy). The quiz derives a grade
 * from a correctness ratio; flashcards take the MIN of a concept's self-rated
 * cards. Both render the same per-concept summary badges.
 */

export type Grade = 1 | 2 | 3 | 4;

export type SubmissionState = 'idle' | 'submitting' | 'done';

/**
 * Per-concept result of the FSRS submit:
 *   'applied'  - this surface's grade landed on the schedule.
 *   'deduped'  - already counted today (the other surface / an earlier attempt
 *                won), so this grade was a no-op.
 *   'pending'  - the concept is not in Mindspace yet: the grade was sent with
 *                its lesson and the server applies it once the concept syncs.
 */
export type ReviewOutcome = 'applied' | 'deduped' | 'pending';
export type ReviewOutcomes = Record<string, ReviewOutcome>;

/** Per-concept FSRS grade, aggregated from a run's per-item results. */
export interface ConceptGrade {
  conceptId: string;
  grade: Grade;
}

/** Quiz: correctness ratio → grade. 100%=Easy, ≥50%=Good, >0%=Hard, 0=Again. */
export function ratioToGrade(ratio: number): Grade {
  if (ratio === 1) return 4;
  if (ratio >= 0.5) return 3;
  if (ratio > 0) return 2;
  return 1;
}

/** Flashcards: the weakest rating for a concept wins (a fumbled card isn't
 *  masked by strong ones). `grades` must be non-empty. */
export function minGrade(grades: Grade[]): Grade {
  return grades.reduce((lo, g) => (g < lo ? g : lo), grades[0]);
}

export function gradeLabel(grade: Grade): string {
  return ['Again', 'Hard', 'Good', 'Easy'][grade - 1];
}

// The status scale as tokens (Easy→success, Good→brand, Hard→warning,
// Again→destructive). Good is the brand accent, indigo in light and violet in
// dark, not `info` blue: blue was a second accent next to the app's indigo,
// and Mindspace already tones Good as indigo. The hue lives in a soft tint
// and a hairline border; the label stays in the ink colour, the same pattern
// as quiz answers. Coloured `-emphasis` text was tried first and `info` could
// not clear 4.5:1 on the indigo surfaces at any tint (4.3 at best).
// Light mode used to be raw emerald/blue/amber/rose while dark used tokens.
export function gradeBadgeClasses(grade: Grade): string {
  switch (grade) {
    case 4:
      return 'border border-success/40 bg-success/10 text-foreground';
    case 3:
      return 'border border-brand-500/40 bg-brand-500/10 text-foreground dark:border-primary/40 dark:bg-primary/15';
    case 2:
      return 'border border-warning/50 bg-warning/10 text-foreground';
    case 1:
      return 'border border-destructive/40 bg-destructive/10 text-foreground';
  }
}

/** What `submitConceptGrades` needs to send one grade. */
export interface GradeSubmitter {
  /** The concept's Mindspace id, when it has synced. */
  canonicalId: (lessonConceptId: string) => string | undefined;
  submit: (vars: {
    conceptId: string;
    grade: 1 | 2 | 3 | 4;
    source: 'practice';
    attemptId: string;
    lessonId?: string;
  }) => Promise<unknown>;
  attemptId: string;
  /** Lets a concept not synced yet be graded by its lesson id. */
  lessonId?: string;
}

/** Send a run's grades: by Mindspace id when synced, otherwise with the
 *  lesson so the server keeps the grade until the concept syncs (never
 *  dropped). Returns each concept's outcome and how many failed. */
export async function submitConceptGrades(
  grades: ConceptGrade[],
  { canonicalId, submit, attemptId, lessonId }: GradeSubmitter
): Promise<{ outcomes: ReviewOutcomes; failed: number }> {
  const outcomes: ReviewOutcomes = {};
  let failed = 0;
  for (const grade of grades) {
    const synced = canonicalId(grade.conceptId);
    if (!synced && !lessonId) continue;
    try {
      const res = (await submit({
        conceptId: synced ?? grade.conceptId,
        grade: grade.grade,
        source: 'practice',
        attemptId,
        lessonId: synced ? undefined : lessonId,
      })) as { deduped?: boolean; pending?: boolean } | undefined;
      outcomes[grade.conceptId] = res?.pending
        ? 'pending'
        : res?.deduped
          ? 'deduped'
          : 'applied';
    } catch {
      failed += 1;
    }
  }
  return { outcomes, failed };
}
